import { defineStore } from "pinia";
import { computed, onBeforeMount, reactive, ref } from "vue";
import { computeSha256, sendFileChunks } from "@/utils";
import { attachFile, deleteFile, finalizeSession, getSession } from "@/api";
import { SchemaUploadingSessionDto, SchemaUserFileDto } from "@/api-types";
import { useIntervalFn } from "@vueuse/core";
import { fileTypeFromBlob } from "file-type";

const blockedMimeTypes = [
    "application/x-msdownload",
    "application/x-msdos-program",
    "application/x-msi",
];

const forbiddenSymbols = ["\\", "/", ":", "*", "?", '"', "<", ">", "|"];
const forbiddenStarts = [".", " ", "_"];
const forbiddenEnds = [".", " ", "_", "-", ".exe", ".msi", ".sh"];

const checkName = (name: string) => {
    name = name.toLowerCase();
    if (name.length === 0) return false;
    if (forbiddenSymbols.some((s) => name.includes(s))) return false;
    if (forbiddenStarts.some((s) => name.startsWith(s))) return false;
    if (forbiddenEnds.some((s) => name.endsWith(s))) return false;
    return true;
};

export type FileWithBlob = SchemaUserFileDto & { attachedFile?: File };

const sessionKey = window.location.pathname.split("/").pop();

const onFileAttached = async (file: FileWithBlob) => {
    console.log(`File ${file.fileName} attached`);
    if (file.isDuplicate) {
        console.log(`File ${file.fileName} is duplicate`);
        return;
    }
    if (!file.attachedFile) {
        console.log(`File ${file.fileName} has no attached file`);
        return;
    }
    if (file.isUploaded) {
        console.log(`File ${file.fileName} is already uploaded`);
        return;
    }

    if (file.hasError) {
        console.log(`File ${file.fileName} has error`);
        return;
    }

    if (file.fileSize === 0) {
        console.log(`File ${file.fileName} has zero size`);
        return;
    }

    if (!sessionKey) {
        console.log(`No session key`);
        return;
    }

    const fileId = file.id;
    console.log(`Sending file ${file.fileName} with id ${fileId}`);
    await sendFileChunks(file.attachedFile, fileId, sessionKey, file.uploadedSize);
};

export const useFilesStore = defineStore("files", () => {
    const currentSession = ref<SchemaUploadingSessionDto | null>(null);
    const fileList = ref<SchemaUserFileDto[]>([]);
    const attachedFiles = ref<Map<string, File>>(new Map());

    const fullFiles = computed<FileWithBlob[]>(() => {
        return fileList.value.map((f) => {
            const attachedFile = attachedFiles.value.get(f.id);
            return {
                ...f,
                attachedFile,
            };
        });
    });

    const hashingTasks = ref<Map<File, number>>(new Map());

    const addFile = async (file: File) => {
        if (!file || !sessionKey) return;
        hashingTasks.value.set(file, 0);
        try {
            console.log(`Adding file ${file.name}`);
            if (!checkName(file.name)) {
                alert(`File ${file.name} is not allowed`);
                return;
            }
            let typeResult = await fileTypeFromBlob(file);

            const type = typeResult?.mime || file.type;
            if (!type) {
                alert(`File ${file.name} has unknown type`);
                return;
            }
            if (blockedMimeTypes.includes(type)) {
                alert(`File ${file.name} has blocked type ${type}`);
                return;
            }
            console.log(`File ${file.name} has type ${type}`);
            const sha256 = await computeSha256(file, (p) => hashingTasks.value.set(file, p));
            console.log(`File ${file.name} has sha256 ${sha256}`);

            const existingFile = fullFiles.value.find((f) => f.hashSha256 === sha256 && f.fileName !== file.name);
            if (existingFile) {
                alert(`File ${file.name} already exists as ${existingFile.fileName}`);
                return;
            }
            const allreadyAttachedFile = fullFiles.value.find(
                (f) => f.hashSha256 === sha256 && f.fileName === file.name && !!f.attachedFile
            );
            if (allreadyAttachedFile) {
                alert(`File ${file.name} already attached`);
                return;
            }
            const response = await attachFile(sessionKey, {
                fileName: file.name,
                hashSha256: sha256,
                fileSize: file.size,
                modifiedAt: new Date(file.lastModified).toISOString(),
            });
            if (response.error) {
                alert(`Failed to attach file ${file.name}: ${response.error}`);
                return;
            }
            const id = response.data;
            if (!id) {
                alert(`Failed to attach file ${file.name}: no id`);
                return;
            }
            attachedFiles.value.set(id, file);
            await updateSession();
            const attachedFile = fullFiles.value.find((f) => f.id === id);
            if (!attachedFile) {
                alert(`Failed to find attached file ${id}`);
                return;
            }
            onFileAttached(attachedFile);
        } finally {
            hashingTasks.value.delete(file);
        }
    };

    const status = reactive({
        isConnected: false,
        isValidSession: false,
        isFinalized: false,
        isFinalizing: false,
        hasFinalizingError: false,
    });
    const validateUUID = (uuid: string) => uuid.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);

    const updateSession = async () => {
        if (status.isFinalized) return;
        if (!sessionKey || !validateUUID(sessionKey)) {
            status.isConnected = true;
            status.isValidSession = false;
            return;
        }

        if (status.isConnected && !status.isValidSession) {
            return;
        }
        const { error, data } = await getSession(sessionKey);

        if (error || !data) {
            status.isConnected = true;
            status.isValidSession = false;
            return;
        }
        currentSession.value = data;
        status.isConnected = true;
        status.isValidSession = true;
        fileList.value = currentSession.value.files;
    };

    const deleteSessionFile = async (file: SchemaUserFileDto) => {
        if (!sessionKey) return;
        await deleteFile(sessionKey, file.id);
        await updateSession();
    };

    const canFinalize = computed(() => {
        if (!currentSession.value) return false;
        if (fullFiles.value.length === 0) return false;
        if (fullFiles.value.some((f) => !f.isUploaded && !f.isDuplicate)) return false;
        if (fullFiles.value.some((f) => f.hasError)) return false;
        if (status.isFinalizing) return false;
        if (status.isFinalized) return false;
        if (hashingTasks.value.size > 0) return false;
        return true;
    });

    const finalize = async () => {
        if (!sessionKey) return;
        status.isFinalizing = true;
        const { response } = await finalizeSession(sessionKey);
        const done = response.ok;
        status.isFinalizing = false;
        status.isFinalized = done;
        status.hasFinalizingError = !done;
    };

    onBeforeMount(async () => {
        await updateSession();
        useIntervalFn(updateSession, 1000);
    });

    window.onbeforeunload = function () {
        return "Are you sure you want to navigate away from this page?";
    };

    return {
        status,
        fullFiles,
        hashingTasks,
        currentSession,

        addFile,
        deleteSessionFile,
        finalize,
        canFinalize,
    };
});
